/* eslint-disable react-refresh/only-export-components -- контекст и useTelegram в одном модуле */
import React, {
  useEffect,
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
  useRef,
} from "react";
import { requestTelegramContactPhone } from "@/shared/utils/telegramRequestContact";

declare global {
  interface Window {
    Telegram?: {
      WebApp: TelegramWebApp;
    };
  }
}

interface TelegramWebApp {
  ready: () => void;
  expand: () => void;
  close: () => void;

  MainButton: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    isActive: boolean;
    show: () => void;
    hide: () => void;
    setText: (text: string) => void;
    setParams: (params: {
      color?: string;
      text_color?: string;
      is_active?: boolean;
    }) => void;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
    enable: () => void;
    disable: () => void;
    showProgress?: (leaveActive?: boolean) => void;
    hideProgress?: () => void;
  };

  BackButton: {
    isVisible: boolean;
    show: () => void;
    hide: () => void;
    onClick: (callback: () => void) => void;
    offClick: (callback: () => void) => void;
  };

  HapticFeedback?: {
    impactOccurred: (
      style: "light" | "medium" | "heavy" | "rigid" | "soft"
    ) => void;
    notificationOccurred: (type: "error" | "success" | "warning") => void;
    selectionChanged: () => void;
  };

  initData: string;
  initDataUnsafe: {
    query_id?: string;
    user?: {
      id: number;
      first_name: string;
      last_name?: string;
      username?: string;
      language_code?: string;
      photo_url?: string;
      is_premium?: boolean;
    };
    receiver?: Record<string, unknown>;
    start_param?: string;
    chat_type?: string;
    chat_instance?: string;
  };

  platform: string;
  version: string;
  colorScheme: "light" | "dark";

  /** Открыть URL во внешнем браузере (актуально для оплаты). */
  openLink?: (url: string, options?: { try_instant_view?: boolean }) => void;

  sendData: (data: string) => void;
  switchInlineQuery: (query: string, choose_chat_types?: string[]) => void;

  showPopup: (
    params: {
      title?: string;
      message: string;
      buttons?: Array<{
        id?: string;
        type?: "default" | "ok" | "close" | "cancel" | "destructive";
        text?: string;
      }>;
    },
    callback?: (buttonId: string) => void
  ) => void;

  showAlert: (message: string, callback?: () => void) => void;
  showConfirm: (
    message: string,
    callback?: (confirmed: boolean) => void
  ) => void;

  enableClosingConfirmation: () => void;
  disableClosingConfirmation: () => void;

  onEvent: (eventType: string, callback: () => void) => void;
  offEvent: (eventType: string, callback: () => void) => void;
  requestContact: (
    callback?: (sent: boolean, webViewEvent?: unknown) => void
  ) => unknown;
}

interface TelegramContextType {
  webApp: TelegramWebApp | null;
  user: TelegramWebApp["initDataUnsafe"]["user"] | null;
  haptics?: {
    impactOccurred: (
      style: "light" | "medium" | "heavy" | "rigid" | "soft"
    ) => void;
    notificationOccurred: (type: "error" | "success" | "warning") => void;
    selectionChanged: () => void;
  };
  mainButton: {
    show: (text?: string) => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    setText: (text: string) => void;
    setColor: (color: string) => void;
    setTextColor: (color: string) => void;
    onClick: (callback: () => void) => () => void;
  };
  backButton: {
    show: () => void;
    hide: () => void;
    onClick: (callback: () => void) => () => void;
  };
  notifications: {
    showAlert: (message: string) => Promise<void>;
    showConfirm: (message: string) => Promise<boolean>;
    showPopup: (params: {
      title?: string;
      message: string;
      buttons?: Array<{
        id?: string;
        type?: "default" | "ok" | "close" | "cancel" | "destructive";
        text?: string;
      }>;
    }) => Promise<string>;
  };
  platform: string;
  version: string;
  isDark: boolean;
  sendData: (data: object) => void;
  ready: boolean;
  close: () => void;
  expand: () => void;
  requestContact: () => Promise<string>;
}

const TelegramContext = createContext<TelegramContextType | null>(null);

export const useTelegram = () => {
  return useContext(TelegramContext);
};

export const TelegramProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [ready, setReady] = useState(false);
  const [, setInitDataPass] = useState(0);
  const initPassTimerRef = useRef<number | undefined>(undefined);

  const webApp =
    typeof window !== "undefined" && window.Telegram?.WebApp
      ? window.Telegram.WebApp
      : null;

  const user = webApp?.initDataUnsafe?.user || null;

  const requestContact = useCallback((): Promise<string> => {
    if (!webApp) {
      return Promise.reject(new Error("Telegram WebApp not available"));
    }
    if (typeof webApp.requestContact !== "function") {
      return Promise.reject(new Error("requestContact method not available"));
    }
    return requestTelegramContactPhone(webApp);
  }, [webApp]);

  useEffect(() => {
    if (!webApp) {
      console.warn("Telegram WebApp is not available");
      return;
    }

    try {
      webApp.ready();
      webApp.expand();
      queueMicrotask(() => {
        setReady(true);
        initPassTimerRef.current = window.setTimeout(
          () => setInitDataPass((n) => n + 1),
          0
        );
      });
    } catch (error) {
      console.error("Error initializing Telegram WebApp:", error);
    }
    return () => {
      if (initPassTimerRef.current !== undefined) {
        clearTimeout(initPassTimerRef.current);
        initPassTimerRef.current = undefined;
      }
    };
  }, [webApp]);

  const haptics = useMemo(() => {
    if (!webApp?.HapticFeedback) return undefined;

    return {
      impactOccurred: (
        style: "light" | "medium" | "heavy" | "rigid" | "soft"
      ) => {
        if (webApp?.HapticFeedback) {
          webApp.HapticFeedback.impactOccurred(style);
        }
      },
      notificationOccurred: (type: "error" | "success" | "warning") => {
        if (webApp?.HapticFeedback) {
          webApp.HapticFeedback.notificationOccurred(type);
        }
      },
      selectionChanged: () => {
        if (webApp?.HapticFeedback) {
          webApp.HapticFeedback.selectionChanged();
        }
      },
    };
  }, [webApp]);

  const mainButton = useMemo(
    () => ({
      show: (text?: string) => {
        if (webApp?.MainButton) {
          if (text) webApp.MainButton.setText(text);
          webApp.MainButton.show();
        }
      },
      hide: () => {
        if (webApp?.MainButton) webApp.MainButton.hide();
      },
      enable: () => {
        if (webApp?.MainButton) webApp.MainButton.enable();
      },
      disable: () => {
        if (webApp?.MainButton) webApp.MainButton.disable();
      },
      setText: (text: string) => {
        if (webApp?.MainButton) webApp.MainButton.setText(text);
      },
      setColor: (color: string) => {
        if (webApp?.MainButton) {
          webApp.MainButton.setParams({ color });
        }
      },
      setTextColor: (color: string) => {
        if (webApp?.MainButton) {
          webApp.MainButton.setParams({ text_color: color });
        }
      },
      onClick: (callback: () => void) => {
        if (webApp?.MainButton) {
          webApp.MainButton.onClick(callback);
          return () => webApp.MainButton?.offClick(callback);
        }
        return () => {};
      },
    }),
    [webApp]
  );

  const backButton = useMemo(
    () => ({
      show: () => {
        if (webApp?.BackButton) webApp.BackButton.show();
      },
      hide: () => {
        if (webApp?.BackButton) webApp.BackButton.hide();
      },
      onClick: (callback: () => void) => {
        if (webApp?.BackButton) {
          webApp.BackButton.onClick(callback);
          return () => webApp.BackButton?.offClick(callback);
        }
        return () => {};
      },
    }),
    [webApp]
  );

  const notifications = useMemo(
    () => ({
      showAlert: (message: string): Promise<void> => {
        return new Promise((resolve) => {
          if (webApp?.showAlert) {
            webApp.showAlert(message, resolve);
          } else {
            alert(message);
            resolve();
          }
        });
      },
      showConfirm: (message: string): Promise<boolean> => {
        return new Promise((resolve) => {
          if (webApp?.showConfirm) {
            webApp.showConfirm(message, resolve);
          } else {
            resolve(window.confirm(message));
          }
        });
      },
      showPopup: (params: {
        title?: string;
        message: string;
        buttons?: Array<{
          id?: string;
          type?: "default" | "ok" | "close" | "cancel" | "destructive";
          text?: string;
        }>;
      }): Promise<string> => {
        return new Promise((resolve) => {
          if (webApp?.showPopup) {
            webApp.showPopup(params, resolve);
          } else {
            const result = window.confirm(params.message);
            resolve(result ? "ok" : "cancel");
          }
        });
      },
    }),
    [webApp]
  );

  const sendData = useCallback(
    (data: object) => {
      if (webApp) {
        webApp.sendData(JSON.stringify(data));
      } else {
        console.log("Send data (mock):", data);
      }
    },
    [webApp]
  );

  const value: TelegramContextType = {
    webApp,
    user,
    haptics,
    mainButton,
    backButton,
    notifications,
    platform: webApp?.platform || "web",
    version: webApp?.version || "0.0",
    isDark: webApp?.colorScheme === "dark",
    sendData,
    ready,
    close: () => webApp?.close(),
    expand: () => webApp?.expand(),
    requestContact,
  };

  return (
    <TelegramContext.Provider value={value}>
      {children}
    </TelegramContext.Provider>
  );
};
